import React, { useState } from "react";
import { toast } from "sonner";
import { PackageCheck, Download, Send, Loader2, AlertTriangle, CheckCircle2, Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, btn, btnPrimary, btnBrand, labelCls, STATUS_TONE, errMsg, blobErr, downloadAuthed, fmtBytes, fmtDateTime, docsLabel } from "@/components/accounting/ui";
import SendConfirm from "@/components/accounting/SendConfirm";
import { DispatchLog, ArchiveList } from "@/components/accounting/DispatchLog";

const Check = ({ ok, warn, children, testId }) => (
  <li className="flex items-start gap-2 text-xs" data-testid={testId}>
    {ok ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-px" /> : <AlertTriangle size={14} className={`${warn ? "text-amber-600" : "text-red-600"} shrink-0 mt-px`} />}
    <span className={ok ? "text-dim" : warn ? "text-amber-800" : "text-red-700"}>{children}</span>
  </li>
);

export const MonthClose = ({ ov, settings, email, reload, onOpenSettings }) => {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const cur = ov.current_export;
  const supp = ov.scope.kind === "nachlieferung";
  const n = ov.scope.doc_count;
  const nm = ov.scope.missing_count;
  const ready = cur && !cur.stale;
  const canSend = ready && email.configured && !!settings.advisor_email;
  const exportsById = Object.fromEntries([...(cur ? [cur] : []), ...ov.exports].map((e) => [e.id, e]));

  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounting/months/${ov.period}/prepare`);
      toast.success(`${data.filename} vorbereitet (${docsLabel(data.doc_count)})`);
      await reload();
    } catch (e) { toast.error(errMsg(e, "Vorbereitung fehlgeschlagen")); }
    setBusy(false);
  };

  const download = async (e) => {
    try { await downloadAuthed(`/admin/accounting/exports/${e.id}/download`, e.filename); }
    catch (err) { toast.error(await blobErr(err, "Download fehlgeschlagen")); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/accounting/exports/${target.id}/send`, { confirm: true });
      if (data.status === "gesendet") toast.success(`Versendet an ${data.to.join(", ")}`);
      else toast.error(`Versand fehlgeschlagen: ${data.error}`);
      setTarget(null);
      await reload();
    } catch (e) { toast.error(errMsg(e, "Versand fehlgeschlagen")); }
    setSending(false);
  };

  const revoke = async (x) => {
    try { await api.post(`/admin/accounting/dispatches/${x.id}/revoke`); toast.success("Link widerrufen"); await reload(); }
    catch (e) { toast.error(errMsg(e, "Widerruf fehlgeschlagen")); }
  };

  return (
    <div className="rounded-xl border border-line bg-surface p-5 md:p-6 space-y-6" data-testid="acc-month-close">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <p className={labelCls}>Monatsabschluss</p>
          <p className="font-display font-bold text-xl text-ink mt-2" data-testid="acc-close-title">{ov.label}{supp && n > 0 ? ` · Nachlieferung ${ov.scope.seq}` : ""}</p>
        </div>
        <Badge tone={STATUS_TONE[ov.status]} testId="acc-close-status">{ov.status_label}</Badge>
      </div>

      <ul className="space-y-2">
        <Check ok={n > 0} warn testId="acc-check-docs">
          {n > 0 ? `${n} ${supp ? (n === 1 ? "neuer Beleg für die Nachlieferung" : "neue Belege für die Nachlieferung") : (n === 1 ? "Dokument für das Monatsarchiv" : "Dokumente für das Monatsarchiv")}`
            : supp ? "Monat übermittelt – keine neuen Belege offen" : "Noch keine Belege erfasst"}
        </Check>
        {nm > 0 && <Check warn testId="acc-check-missing">{nm} {nm === 1 ? "Beleg" : "Belege"} mit fehlenden Angaben (Versand trotzdem möglich)</Check>}
        {n > 0 && ov.duplicate_count > 0 && <Check warn testId="acc-check-duplicates">{ov.duplicate_count} {ov.duplicate_count === 1 ? "Dokument ist ein mögliches Duplikat" : "Dokumente sind mögliche Duplikate"}</Check>}
        <Check ok={email.configured} testId="acc-check-email">{email.configured ? `E-Mail-Dienst eingerichtet (${email.provider})` : `E-Mail-Dienst nicht eingerichtet – Versand deaktiviert (${email.detail})`}</Check>
        <Check ok={!!settings.advisor_email} testId="acc-check-advisor">
          {settings.advisor_email ? `Steuerberater: ${settings.advisor_email}` : "Keine Steuerberater-Adresse hinterlegt"}
          {!settings.advisor_email && <button type="button" onClick={onOpenSettings} className="ml-2 underline font-semibold" data-testid="acc-open-settings-inline"><Settings2 size={11} className="inline -mt-0.5" /> Einstellungen</button>}
        </Check>
      </ul>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={prepare} disabled={busy || n === 0} className={btnPrimary} data-testid="acc-prepare-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={15} />}
          {cur ? "Neu vorbereiten" : supp ? "Nachlieferung vorbereiten" : "Monatsunterlagen vorbereiten"}
        </button>
      </div>

      {cur && (
        <div className={`rounded-lg border px-4 py-4 ${cur.stale ? "border-amber-500/40 bg-amber-500/5" : "border-line bg-elevated/40"}`} data-testid="acc-current-export">
          <p className="text-sm text-ink font-semibold break-all" data-testid="acc-current-export-name">{cur.filename}</p>
          <p className="text-[11px] text-faint mt-1">
            {docsLabel(cur.doc_count)} ({cur.counts.einkauf} Einkauf · {cur.counts.verkauf} Verkauf · {cur.counts.bank} Bank) · {fmtBytes(cur.zip_size || cur.total_size)} · erstellt {fmtDateTime(cur.created_at)}
          </p>
          {cur.stale && <p className="text-xs text-amber-800 mt-2" data-testid="acc-export-stale">Belege wurden seit der Vorbereitung geändert – bitte neu vorbereiten.</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" onClick={() => download(cur)} className={btn} data-testid="acc-export-download-btn"><Download size={12} /> ZIP herunterladen</button>
            <button type="button" onClick={() => setTarget(cur)} disabled={!canSend} className={btnBrand} data-testid="acc-send-btn"><Send size={14} /> An Steuerberater senden</button>
          </div>
        </div>
      )}

      <DispatchLog dispatches={ov.dispatches} exportsById={exportsById} onResend={(e) => (canSend || (e.status === "uebermittelt" && email.configured && settings.advisor_email) ? setTarget(e) : toast.error("Versand derzeit nicht möglich – bitte Hinweise oben prüfen"))} onRevoke={revoke} />
      <ArchiveList exports={ov.exports.filter((e) => e.status === "uebermittelt")} onDownload={download} />
      <SendConfirm target={target} settings={settings} sending={sending} onCancel={() => setTarget(null)} onConfirm={send} />
    </div>
  );
};

export default MonthClose;
